/**
 * Step 5 — Attack Path Feasibility.
 *
 * Story 7.A: spinner / failed-hero / table dispatcher; Add / Edit /
 * Delete via the shared FormModal + useConfirm dialog; attack-vector
 * filter via FilterDrawer; hierarchical Current/Child split when
 * `analysis_mode === 'hierarchical'`.
 *
 * On save, the form's semicolon-separated `preconditions` textarea
 * is converted back to `string[]` via `normaliseAttackPathRecord`.
 * The five Annex G factor numerics + `total_attack_potential` are
 * recomputed by the backend on save.
 */
import React, { useState } from 'react';
import { LoadingState, FailedState } from '../../../components/ui/LoadingState';

import { useConfirm } from '../../../components/ui/AlertDialog';
import { Icon } from '../../../components/ui/Icon';
import AttackPathTable from './step5-attack-paths/AttackPathTable';
import AttackPathFormModal from './step5-attack-paths/AttackPathFormModal';
import HierarchicalAttackPathReview from './step5-attack-paths/HierarchicalAttackPathReview';
import type { ChildAttackPathView } from './step5-attack-paths/ChildAttackPathsTable';
import {
  normaliseAttackPathRecord,
  type AttackPathRecord,
} from './step5-attack-paths/attackPathFieldSchema';
import {
  exportAttackPathsToCsv,
  parseAttackPathFile,
} from './step5-attack-paths/attackPathCsv';
import type { Step5BodyProps } from './stepBodyProps';
import { notify } from '../../../services/notificationBus';
import styles from './Step5AttackPathFeasibility.module.css';

// LoadingState/FailedState are JSX primitives; cast for use from TSX
// (mirrors the ButtonAny convention across the step-components).
const LoadingStateAny = LoadingState as unknown as React.ComponentType<any>;
const FailedStateAny = FailedState as unknown as React.ComponentType<any>;


type ModalState =
  | null
  | { kind: 'add' }
  | { kind: 'edit'; path: AttackPathRecord };

export default function Step5AttackPathFeasibility({
  status,
  editable,
  structured,
  crud,
  threatOptions,
  assetOptions,
  hierarchical,
  errorMessage,
}: Step5BodyProps) {
  const [modal, setModal] = useState<ModalState>(null);
  const { confirm, confirmDialog } = useConfirm();

  if (status === 'failed') {
    return (
      <FailedStateAny
        variant="page"
        title="Attack path analysis failed"
        description={errorMessage ?? structured.error ?? 'The attack path agent encountered an error. Revise and retry.'}
      />
    );
  }

  const paths = (structured.data ?? []) as AttackPathRecord[];

  const noDataYet = paths.length === 0 && !structured.data;
  if (
    status === 'empty' ||
    status === 'generating' ||
    status === 'revising' ||
    (structured.loading && noDataYet)
  ) {
    return (
      <LoadingStateAny
        variant="page"
        title="Analysing attack paths"
        description="The attack path agent is scoring feasibility per ISO/SAE 21434 Annex G. This usually takes 1–3 minutes."
      />
    );
  }

  const handleSave = async (values: Record<string, unknown>) => {
    const patched = normaliseAttackPathRecord(values);
    try {
      if (modal?.kind === 'edit') {
        await crud.update(modal.path.attack_path_id, patched);
        notify({ type: 'success', message: `Attack path "${patched.attack_path_id}" updated.` });
      } else {
        await crud.add(patched);
        notify({ type: 'success', message: `Attack path "${patched.attack_path_id}" added.` });
      }
      setModal(null);
      structured.refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to save attack path';
      throw new Error(typeof msg === 'string' ? msg : 'Failed to save attack path');
    }
  };

  const handleExport = () => {
    // Export only current-TARA rows in hierarchical mode — child rows
    // live in their upstream session's export.
    exportAttackPathsToCsv(
      hierarchical.enabled
        ? paths.filter((p: any) => p?.source !== 'child_tara')
        : paths,
    );
  };

  const handleImport = async (file: File) => {
    let records: AttackPathRecord[];
    try {
      records = await parseAttackPathFile(file);
    } catch (e: any) {
      notify({
        type: 'error',
        title: 'Import failed',
        message: e?.message || `Could not parse ${file.name}`,
      });
      return;
    }
    if (records.length === 0) {
      notify({ type: 'warning', message: `${file.name} contained no attack-path rows.` });
      return;
    }

    const existingIds = new Set(paths.map((p) => p.attack_path_id));
    let added = 0;
    let skipped = 0;
    let failed = 0;
    for (const record of records) {
      if (!record?.attack_path_id) {
        failed += 1;
        continue;
      }
      if (existingIds.has(record.attack_path_id)) {
        skipped += 1;
        continue;
      }
      try {
        const patched = normaliseAttackPathRecord(record as Record<string, unknown>);
        await crud.add(patched as Record<string, unknown>);
        existingIds.add(record.attack_path_id);
        added += 1;
      } catch {
        failed += 1;
      }
    }

    if (added > 0) structured.refetch();
    const parts: string[] = [];
    if (added) parts.push(`${added} added`);
    if (skipped) parts.push(`${skipped} skipped (duplicate id)`);
    if (failed) parts.push(`${failed} failed`);
    notify({
      type: failed && !added ? 'error' : 'success',
      title: failed && !added ? 'Import failed' : 'Import complete',
      message: parts.join(' · ') || 'No rows processed.',
    });
  };

  const handleDelete = async (target: AttackPathRecord) => {
    const ok = await confirm({
      title: 'Delete attack path?',
      description: `Delete attack path "${target.attack_path_id}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      media: <Icon name="delete" />,
    });
    if (!ok) return;
    try {
      await crud.remove(target.attack_path_id);
      notify({ type: 'success', message: `Attack path "${target.attack_path_id}" deleted.` });
      structured.refetch();
    } catch (e: any) {
      const msg = e?.message || 'Failed to delete attack path';
      notify({ type: 'error', title: 'Delete failed', message: msg });
    }
  };

  const newPaths = hierarchical.enabled
    ? paths.filter((p: any) => p?.source !== 'child_tara')
    : paths;
  const childPaths: ChildAttackPathView[] = hierarchical.enabled
    ? paths.filter((p: any) => p?.source === 'child_tara')
    : [];

  return (
    <div className={styles.bleed}>
      {hierarchical.enabled ? (
        <HierarchicalAttackPathReview
          newPaths={newPaths}
          childPaths={childPaths}
          editable={editable}
          onAdd={() => setModal({ kind: 'add' })}
          onEdit={(path) => setModal({ kind: 'edit', path })}
          onDelete={(path) => handleDelete(path)}
          onExport={handleExport}
          onImport={handleImport}
          modifications={hierarchical.modifications}
          onToggleRelevance={hierarchical.setRelevance}
          onJustificationChange={hierarchical.setJustification}
          childInputsDisabled={hierarchical.approving || !editable}
          onSaveChildModifications={hierarchical.onApprove}
        />
      ) : (
        <AttackPathTable
          paths={paths}
          editable={editable}
          onAdd={() => setModal({ kind: 'add' })}
          onEdit={(path) => setModal({ kind: 'edit', path })}
          onDelete={(path) => handleDelete(path)}
          onExport={handleExport}
          onImport={handleImport}
        />
      )}
      {modal && (
        <AttackPathFormModal
          mode={modal.kind}
          initialPath={modal.kind === 'edit' ? modal.path : null}
          threatOptions={threatOptions}
          assetOptions={assetOptions}
          onSubmit={handleSave}
          onClose={() => setModal(null)}
        />
      )}
      {confirmDialog}
    </div>
  );
}
